import { Alert, Button, ErrorAlert, cx } from "@unizpay/ui";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import type { CheckoutMethod } from "../types";
import { CoinIcon, NetworkIcon, coinName } from "./CoinIcon";

/** What issuing an address needs; the display name is not part of it. */
export type MethodChoice = Pick<CheckoutMethod, "token" | "network">;

const focusRing =
  "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring";

/** The selection mark at the end of a coin row. */
function Radio({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "flex size-[1.125rem] shrink-0 items-center justify-center rounded-full border transition-colors",
        on ? "border-fg bg-fg" : "border-line-strong bg-surface",
      )}
    >
      {on && <span className="size-1.5 rounded-full bg-surface" />}
    </span>
  );
}

/**
 * Step 1: pick the network the funds are on, then the coin, then Continue. Nothing is
 * issued until Continue, so browsing the options never creates deposit addresses.
 */
export function MethodStep({
  methods,
  initial,
  onContinue,
  submitting,
  error,
}: {
  methods: CheckoutMethod[];
  initial: MethodChoice | null;
  onContinue: (choice: MethodChoice) => void;
  submitting: boolean;
  error: unknown;
}) {
  const networks = [...new Map(methods.map((m) => [m.network, m.network_name])).entries()];
  const [network, setNetwork] = useState(initial?.network ?? networks[0]?.[0] ?? "");
  const coins = methods.filter((m) => m.network === network);
  const [token, setToken] = useState(initial?.token ?? coins[0]?.token ?? "");
  const selected = coins.find((m) => m.token === token) ?? null;

  if (methods.length === 0) {
    return (
      <Alert tone="warn" title="No payment methods available">
        This merchant has not enabled any payment methods yet. Please contact them to complete your purchase.
      </Alert>
    );
  }

  function pickNetwork(next: string) {
    setNetwork(next);
    // Keep the coin if the new network has it (USDC is on most networks), else take the first.
    const onNext = methods.filter((m) => m.network === next);
    if (!onNext.some((m) => m.token === token)) setToken(onNext[0]?.token ?? "");
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (selected) onContinue({ token: selected.token, network: selected.network });
      }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-lg font-semibold tracking-tight">Choose how to pay</h1>
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-medium">Network</legend>
        <div className="grid grid-cols-2 gap-2">
          {networks.map(([slug, name]) => {
            const active = slug === network;
            return (
              <label
                key={slug}
                className={cx(
                  "flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 transition-colors",
                  focusRing,
                  active
                    ? "border-fg bg-surface shadow-[0_0_0_1px_var(--fg)]"
                    : "border-line bg-surface hover:border-line-strong hover:bg-bg",
                )}
              >
                <input
                  type="radio"
                  name="network"
                  value={slug}
                  checked={active}
                  onChange={() => pickNetwork(slug)}
                  className="sr-only"
                />
                <NetworkIcon network={slug} size={20} />
                <span className="min-w-0 text-sm leading-tight font-medium">{name}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="mb-2 text-sm font-medium">Coin</legend>
        <div className="divide-y divide-line overflow-hidden rounded-lg border border-line">
          {coins.map((m) => {
            const active = m.token === token;
            return (
              <label
                key={m.token}
                className={cx(
                  "flex cursor-pointer items-center gap-3 px-3.5 py-3 transition-colors",
                  focusRing,
                  active ? "bg-bg" : "hover:bg-bg",
                )}
              >
                <input
                  type="radio"
                  name="coin"
                  value={m.token}
                  checked={active}
                  onChange={() => setToken(m.token)}
                  className="sr-only"
                />
                <CoinIcon symbol={m.token} size={28} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{m.token}</span>
                  <span className="block truncate text-xs text-fg-muted">{coinName(m.token)}</span>
                </span>
                <Radio on={active} />
              </label>
            );
          })}
        </div>
      </fieldset>

      <ErrorAlert error={error} title="Could not prepare this payment method" />

      <Button type="submit" size="lg" className="w-full" disabled={!selected} loading={submitting}>
        {selected ? `Continue with ${selected.token}` : "Choose a coin"}
        {!submitting && <ArrowRight aria-hidden="true" className="size-4" />}
      </Button>
    </form>
  );
}
